import type { PurchaseDocumentType } from '@/generated/enums.gen';
import { erpListRoute, erpRecordRoute, erpResourceBasePath } from '@/definitions/routeTable';
import { erpDefinitionsLoaded } from '@/definitions/store';
import { resourcePermission } from '@/permissions';

const definitions = [
  ['inquiry', 'purchaseInquiries', 'purchase_inquiries'],
  ['quote', 'purchaseQuotes', 'purchase_quotes'],
  ['order', 'purchaseOrders', 'purchase_orders'],
  ['return_order', 'purchaseReturnOrders', 'purchase_return_orders'],
  ['invoice', 'purchaseInvoices', 'purchase_invoices'],
  ['credit_memo', 'purchaseCreditMemos', 'purchase_credit_memos'],
  ['blanket_order', 'purchaseBlanketOrders', 'purchase_blanket_orders'],
] as const;

export type PurchaseLayoutKey = (typeof definitions)[number][1];
export type PurchaseIndexLayoutKey = `${PurchaseLayoutKey}Index`;

/** One purchase mask; the type contributes routes, names and capabilities. */
export interface PurchaseDocKind {
  id: PurchaseDocumentType;
  layoutKey: PurchaseLayoutKey;
  indexLayoutKey: PurchaseIndexLayoutKey;
  entityType: string;
  restBasePath: string;
  listPath: string;
  listRouteName: string;
  showRouteName: string;
  labelKey: string;
  pageKey: string;
  incoming: boolean;
  readPermission: string;
  createPermission: string;
  updatePermission: string;
}

export const PURCHASE_DOC_KINDS: readonly PurchaseDocKind[] = definitions.map(
  ([id, layoutKey, entityType]) => {
    return {
      id,
      layoutKey,
      entityType,
      indexLayoutKey: `${layoutKey}Index`,
      // This registry is imported by the eager shell slice. Read the served
      // routes only on access, after definitions are loaded.
      get restBasePath() {
        return erpResourceBasePath(layoutKey);
      },
      get listPath() {
        return erpListRoute(layoutKey).path;
      },
      get listRouteName() {
        return erpListRoute(layoutKey).name;
      },
      get showRouteName() {
        return erpRecordRoute(layoutKey).name;
      },
      labelKey: `purchase.kinds.${id}`,
      pageKey: `${entityType}_show`,
      incoming: id === 'quote' || id === 'invoice' || id === 'credit_memo',
      readPermission: resourcePermission(`/${entityType}`, 'read'),
      createPermission: resourcePermission(`/${entityType}`, 'create'),
      updatePermission: resourcePermission(`/${entityType}`, 'update'),
    };
  }
);

/** Palette targets use served routes and texts, independent of the menu layout. */
export function purchaseCommandTargets() {
  if (!erpDefinitionsLoaded.value) return [];
  return [
    {
      id: 'demands',
      path: erpListRoute('demandPlanning').path,
      labelKey: 'menu.entries.demandPlanning',
      requiredPermissions: 'demands.read',
    },
    ...PURCHASE_DOC_KINDS.map((kind) => ({
      id: kind.id,
      path: kind.listPath,
      labelKey: `menu.entries.${kind.layoutKey}`,
      requiredPermissions: kind.readPermission,
    })),
  ];
}

export const PURCHASE_LAYOUT_KEYS = PURCHASE_DOC_KINDS.flatMap((k) => [
  k.layoutKey,
  k.indexLayoutKey,
]);

export function purchaseDocKind(id: string): PurchaseDocKind {
  const kind = PURCHASE_DOC_KINDS.find((k) => k.id === id);
  if (!kind) throw new Error(`Unknown purchase document kind: ${id}`);
  return kind;
}

export function purchaseKindForLayout(key: string): PurchaseDocKind | undefined {
  return PURCHASE_DOC_KINDS.find((k) => k.layoutKey === key || k.indexLayoutKey === key);
}
